import { ref, computed, nextTick, watch, onMounted, onUnmounted, type Ref, type ComputedRef } from 'vue';
import type { MenuItem } from '@/utils/menu-config';

export interface UseMenuHeightCalculationReturn {
  menuContainerRef: Ref<HTMLElement | null>;
  bottomContainerRef: Ref<HTMLElement | null>;
  visibleMenus: Ref<MenuItem[]>;
  hiddenMenus: Ref<MenuItem[]>;
  updateVisibleMenus: () => void;
  debouncedUpdateVisibleMenus: () => void;
}

// Debounce helper
const debounce = (fn: Function, delay: number) => {
  let timer: number | null = null;
  return (...args: any[]) => {
    if (timer) clearTimeout(timer);
    timer = window.setTimeout(() => { fn.apply(null, args); timer = null; }, delay);
  };
};

export function useMenuHeightCalculation(
  menus: ComputedRef<MenuItem[]>,
  current: Ref<string>
): UseMenuHeightCalculationReturn {
  const menuContainerRef = ref<HTMLElement | null>(null);
  const bottomContainerRef = ref<HTMLElement | null>(null);
  const visibleMenus = ref<MenuItem[]>([]);
  const hiddenMenus = ref<MenuItem[]>([]);

  const updateVisibleMenus = () => {
    if (!menuContainerRef.value || !bottomContainerRef.value) return;
    const currentMenus = menus.value;
    const containerHeight = menuContainerRef.value.clientHeight;
    const menuItemHeight = 77;
    const moreButtonHeight = 30;
    const headerHeight = 70;
    const bottomHeight = bottomContainerRef.value.clientHeight;
    const availableHeight = containerHeight - headerHeight - bottomHeight;
    if (import.meta.env.DEV) console.log('🔍 Menu Height Debug:', { containerHeight, headerHeight, bottomHeight, availableHeight, menuItemHeight, currentMenusLength: currentMenus.length });
    const maxVisibleItems = Math.floor(availableHeight / menuItemHeight);
    if (import.meta.env.DEV) console.log('📊 Calculation:', { maxVisibleItems, availableHeight, spaceForAllMenus: currentMenus.length * menuItemHeight, wouldNeedMoreButton: maxVisibleItems < currentMenus.length });
    const spaceNeededForAllMenus = currentMenus.length * menuItemHeight;
    const pixelTolerance = 2;
    const canFitAllMenus = spaceNeededForAllMenus <= (availableHeight + pixelTolerance);
    if (canFitAllMenus) {
      visibleMenus.value = [...currentMenus];
      hiddenMenus.value = [];
      if (import.meta.env.DEV) console.log('✅ Showing all menus:', currentMenus.length);
    } else {
      const availableHeightWithMore = availableHeight - moreButtonHeight;
      const maxItemsWithMore = Math.floor(availableHeightWithMore / menuItemHeight);
      if (import.meta.env.DEV) console.log('🔄 Need More Button:', { spaceNeededForAllMenus, availableHeight, availableHeightWithMore, maxItemsWithMore, shortfall: spaceNeededForAllMenus - availableHeight });
      const finalMaxItems = Math.max(2, maxItemsWithMore);
      const currentMenuIndex = currentMenus.findIndex(m => m.path === current.value);
      let adjustedMenus = [...currentMenus];
      if (currentMenuIndex >= finalMaxItems) {
        const currentMenu = adjustedMenus.splice(currentMenuIndex, 1)[0];
        adjustedMenus.splice(finalMaxItems - 1, 0, currentMenu);
      }
      visibleMenus.value = adjustedMenus.slice(0, finalMaxItems);
      hiddenMenus.value = adjustedMenus.slice(finalMaxItems);
    }
  };

  const debouncedUpdateVisibleMenus = debounce(updateVisibleMenus, 100);

  watch(() => menus.value, () => {
    nextTick(() => { updateVisibleMenus(); });
  }, { immediate: true });

  onMounted(() => {
    window.addEventListener('resize', debouncedUpdateVisibleMenus);
    nextTick().then(() => { setTimeout(() => { updateVisibleMenus(); }, 50); });
  });

  onUnmounted(() => {
    window.removeEventListener('resize', debouncedUpdateVisibleMenus);
  });

  return {
    menuContainerRef,
    bottomContainerRef,
    visibleMenus,
    hiddenMenus,
    updateVisibleMenus,
    debouncedUpdateVisibleMenus,
  };
}
